/**
 * Mobile Variant Selector - Bottom Sheet Web Component
 *
 * Reusable mobile variant selector that can be triggered from anywhere.
 *
 * Open:
 *   document.dispatchEvent(new CustomEvent('mobile-variant-selector:open', {
 *     detail: {
 *       product: {
 *         title: 'Product Title',
 *         price: '₹1,883',
 *         compareAtPrice: '₹2,690',       // optional
 *         discountPercent: '30',            // optional
 *         image: { src: '...', alt: '...' },
 *         variants: [
 *           { id: 123, title: 'XS', available: true },
 *           { id: 124, title: 'S', available: true },
 *           { id: 125, title: 'M', available: false },
 *         ],
 *         selectedVariantId: 123,           // optional, defaults to first available
 *         url: '/products/product-handle'   // optional, for image link
 *       },
 *       onSelect: function(variantId, variantTitle) { ... }  // callback when variant is confirmed
 *     }
 *   }));
 *
 * Close programmatically:
 *   document.dispatchEvent(new CustomEvent('mobile-variant-selector:close'));
 */

if (!customElements.get('mobile-variant-selector')) {
  // Singleton: only one instance should handle events even if multiple are rendered
  let _activeInstance = null;

  customElements.define(
    'mobile-variant-selector',
    class MobileVariantSelector extends HTMLElement {
      constructor() {
        super();
        this._isInert = false;
        this.selectedVariantId = null;
        this.onSelectCallback = null;
        this.productData = null;

        // Cache DOM refs
        this.overlay = this.querySelector('.mvs__overlay');
        this.closeBtn = this.querySelector('.mvs__close');
        this.productImage = this.querySelector('.mvs__product-image');
        this.productImageLink = this.querySelector('.mvs__product-image-link');
        this.productTitle = this.querySelector('.mvs__product-title');
        this.priceSale = this.querySelector('.mvs__price-sale');
        this.priceCompare = this.querySelector('.mvs__price-compare');
        this.priceDiscount = this.querySelector('.mvs__price-discount');
        this.variantList = this.querySelector('.mvs__variant-list');
        this.selectBtn = this.querySelector('.mvs__select-btn');
        this.selectBtnText = this.querySelector('.mvs__select-btn-text');
        this.spinner = this.querySelector('.mvs__spinner');

        // Bind methods
        this.handleOpen = this.handleOpen.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.close = this.close.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleSelectClick = this.handleSelectClick.bind(this);
      }

      connectedCallback() {
        // If another instance is already active, make this one inert
        if (_activeInstance) {
          this._isInert = true;
          return;
        }
        _activeInstance = this;

        document.addEventListener('mobile-variant-selector:open', this.handleOpen);
        document.addEventListener('mobile-variant-selector:close', this.handleClose);
        this.overlay.addEventListener('click', this.close);
        this.closeBtn.addEventListener('click', this.close);
        this.selectBtn.addEventListener('click', this.handleSelectClick);
      }

      disconnectedCallback() {
        if (this._isInert) return;
        if (_activeInstance === this) {
          _activeInstance = null;
        }
        document.removeEventListener('mobile-variant-selector:open', this.handleOpen);
        document.removeEventListener('mobile-variant-selector:close', this.handleClose);
        this.overlay.removeEventListener('click', this.close);
        this.closeBtn.removeEventListener('click', this.close);
        this.selectBtn.removeEventListener('click', this.handleSelectClick);
      }

      handleOpen(event) {
        const { product, onSelect } = event.detail || {};
        if (!product) return;

        this.productData = product;
        this.onSelectCallback = onSelect || null;
        this.render(product);
        this.open();
      }

      handleClose() {
        this.close();
      }

      render(product) {
        // Image
        if (product.image && product.image.src) {
          this.productImage.src = product.image.src;
          this.productImage.alt = product.image.alt || product.title || '';
        }

        // Image link
        if (product.url) {
          this.productImageLink.href = product.url;
        } else {
          this.productImageLink.removeAttribute('href');
        }

        // Title
        this.productTitle.textContent = product.title || '';

        // Price
        this.priceSale.textContent = product.price || '';
        this.priceCompare.textContent = product.compareAtPrice || '';
        this.priceDiscount.textContent = product.discountPercent
          ? product.discountPercent + '% OFF'
          : '';

        // Variants
        this.renderVariants(product.variants || [], product.selectedVariantId);

        // Reset button state
        this.setLoading(false);
        this.selectBtn.disabled = !this.selectedVariantId;
      }

      renderVariants(variants, preselectedId) {
        this.variantList.innerHTML = '';
        this.selectedVariantId = null;

        // Determine which variant to preselect
        let selectId = preselectedId;
        if (!selectId) {
          const firstAvailable = variants.find((v) => v.available);
          selectId = firstAvailable ? firstAvailable.id : null;
        }

        variants.forEach((variant) => {
          const li = document.createElement('li');
          li.className = 'mvs__variant-item';

          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'mvs__variant-btn';
          btn.role = 'radio';
          btn.textContent = variant.title;
          btn.dataset.variantId = variant.id;
          btn.dataset.available = String(variant.available !== false);

          const isSelected = variant.id === selectId && variant.available !== false;
          btn.setAttribute('aria-checked', String(isSelected));

          if (isSelected) {
            this.selectedVariantId = variant.id;
          }

          if (variant.available === false) {
            btn.setAttribute('aria-disabled', 'true');
          }

          btn.addEventListener('click', () => this.selectVariant(btn, variant));

          li.appendChild(btn);
          this.variantList.appendChild(li);
        });
      }

      selectVariant(btn, variant) {
        if (variant.available === false) return;

        // Deselect all
        this.variantList.querySelectorAll('.mvs__variant-btn').forEach((b) => {
          b.setAttribute('aria-checked', 'false');
        });

        // Select this one
        btn.setAttribute('aria-checked', 'true');
        this.selectedVariantId = variant.id;
        this.selectBtn.disabled = false;
      }

      handleSelectClick() {
        if (!this.selectedVariantId) return;

        const selectedBtn = this.variantList.querySelector(
          `.mvs__variant-btn[aria-checked="true"]`
        );
        const variantTitle = selectedBtn ? selectedBtn.textContent : '';

        if (this.onSelectCallback) {
          this.onSelectCallback(this.selectedVariantId, variantTitle);
        }

        // Dispatch event for listeners that aren't using callback
        this.dispatchEvent(
          new CustomEvent('variant-selected', {
            bubbles: true,
            detail: {
              variantId: this.selectedVariantId,
              variantTitle: variantTitle,
              product: this.productData,
            },
          })
        );

        this.close();
      }

      open() {
        this.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.handleKeydown);

        // Trap focus - focus close button
        requestAnimationFrame(() => {
          this.closeBtn.focus();
        });
      }

      close() {
        this.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        document.removeEventListener('keydown', this.handleKeydown);
      }

      handleKeydown(event) {
        if (event.key === 'Escape') {
          this.close();
        }
      }

      /** Expose loading state for external use (e.g. during add-to-cart) */
      setLoading(loading) {
        if (loading) {
          this.selectBtn.disabled = true;
          this.selectBtnText.style.visibility = 'hidden';
          this.spinner.classList.remove('hidden');
        } else {
          this.selectBtn.disabled = !this.selectedVariantId;
          this.selectBtnText.style.visibility = '';
          this.spinner.classList.add('hidden');
        }
      }
    }
  );
}
